import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import { base44 } from "api/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "components/ui/table";
import {
  Plus, Package, Trash2, AlertCircle, Pencil, AlertTriangle,
  CheckCircle, Truck, ShoppingBag, TrendingDown, Building2,
} from "lucide-react";
import { Badge } from "components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle,
} from "components/ui/alert-dialog";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Textarea } from "components/ui/textarea";
import { format, addDays, startOfWeek, endOfWeek, isToday, parseISO } from "date-fns";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import {
  buildInventoryItemPayload,
  formatInventoryNumber,
  getDerivedCostPerUnit,
  getOrderDraft,
  getPurchasePlan,
  getPurchaseUnitLabel,
  normalizeInventoryItemForm,
} from "utils";

// ─── New Order Form default ───
const EMPTY_ORDER_FORM = {
  inventory_item_id: '',
  inventory_item_name: '',
  article_number: '',
  package_label: '',
  suggested_quantity: '',
  unit: '',
  supplier_id: '',
  supplier: '',
  order_date: format(new Date(), 'yyyy-MM-dd'),
  expected_delivery_date: '',
  estimated_cost: '',
  status: '',
  notes: '',
};

// ─── Stock bar helper ───
function StockBar({ current, reorder, unit }) {
  const pct = reorder > 0 ? Math.min((current / reorder) * 100, 100) : 100;
  const color = pct < 30 ? 'bg-red-500' : pct < 60 ? 'bg-orange-400' : 'bg-green-500';
  return (
    <div className="flex items-center gap-2">
      <div className="w-24 h-2.5 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-600 whitespace-nowrap">
        {current}{unit} / {reorder}{unit}
      </span>
    </div>
  );
}

function convertQtyToInventoryUnit(qty, recipeUnit, inventoryUnit) {
  if (!recipeUnit || !inventoryUnit || recipeUnit === inventoryUnit) return qty;
  const massToG = { g: 1, kg: 1000, mg: 0.001 };
  const volToMl = { ml: 1, L: 1000, l: 1000, cl: 10, dl: 100 };
  if (massToG[recipeUnit] && massToG[inventoryUnit]) return qty * massToG[recipeUnit] / massToG[inventoryUnit];
  if (volToMl[recipeUnit] && volToMl[inventoryUnit]) return qty * volToMl[recipeUnit] / volToMl[inventoryUnit];
  return qty;
}

function getExpectedDeliveryDate(orderDate, leadTimeDays) {
  if (!orderDate || leadTimeDays == null || leadTimeDays === '') return '';
  const numericLeadTime = Number(leadTimeDays);
  if (!Number.isFinite(numericLeadTime) || numericLeadTime < 0) return '';
  return format(addDays(new Date(orderDate), numericLeadTime), 'yyyy-MM-dd');
}

export default function Stock() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  // ─── Tab state from URL ───
  const initialTab = searchParams.get('tab') || 'products';
  const [activeTab, setActiveTab] = useState(initialTab);
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams(tab === 'products' ? {} : { tab });
  };

  // ─── Inventory dialog state ───
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [newItem, setNewItem] = useState(normalizeInventoryItemForm());
  const [isSupplierDialogOpen, setIsSupplierDialogOpen] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [deleteSupplierId, setDeleteSupplierId] = useState(null);
  const [newSupplier, setNewSupplier] = useState({ name: '', lead_time_days: '' });

  // ─── Loss dialog state ───
  const [isLossDialogOpen, setIsLossDialogOpen] = useState(false);
  const [newLoss, setNewLoss] = useState({
    inventory_item_name: '', quantity: '', unit: 'kg',
    date: format(new Date(), 'yyyy-MM-dd'), reason: 'Produktionsfehler',
    description: '', cost: '', shift: 'Abend', reported_by: '',
  });

  // ─── New order dialog state ───
  const [newOrderDialog, setNewOrderDialog] = useState(false);
  const [orderForm, setOrderForm] = useState(EMPTY_ORDER_FORM);

  // ─── Orders filter state ───
  const [ordersFilter, setOrdersFilter] = useState('active');

  const queryClient = useQueryClient();

  // ────────────────────────────────────────────
  // QUERIES
  // ────────────────────────────────────────────
  const { data: inventory, isLoading } = useQuery({
    queryKey: ['inventory'],
    queryFn: () => base44.entities.InventoryItem.list(),
    initialData: [],
  });

  const { data: losses, isLoading: loadingLosses } = useQuery({
    queryKey: ['productionlosses'],
    queryFn: () => base44.entities.ProductionLoss.list('-date'),
    initialData: [],
  });

  const { data: orders, isLoading: loadingOrders } = useQuery({
    queryKey: ['orders'],
    queryFn: () => base44.entities.Order.list('-order_date'),
    initialData: [],
  });

  const { data: suppliers = [], isLoading: loadingSuppliers } = useQuery({
    queryKey: ['suppliers'],
    queryFn: () => base44.entities.Supplier.list('name'),
    initialData: [],
  });

  const { data: recipes = [] } = useQuery({
    queryKey: ['recipes'],
    queryFn: () => base44.entities.Recipe.list(),
    initialData: [],
  });

  const { data: forecastResponse } = useQuery({
    queryKey: ['forecast', 'guests', 7],
    queryFn: () => base44.forecast.getGuests(7),
    initialData: { forecasts: [] },
    staleTime: 5 * 60 * 1000,
  });

  // Projected stock depletion over next 7 days based on guest forecast × recipe ingredients
  const stockAlerts = useMemo(() => {
    const forecasts = forecastResponse?.forecasts || [];
    if (!forecasts.length || !recipes.length || !inventory?.length) return [];

    const totalServings = recipes.reduce((s, r) => s + (parseFloat(r.servings) || 1), 0);
    if (totalServings === 0) return [];

    // Accumulate consumption per inventory item id
    const consumption = {};
    forecasts.forEach(day => {
      if (day.is_closed) return;
      const guests = parseFloat(day.guests) || 0;
      recipes.forEach(recipe => {
        const servings = parseFloat(recipe.servings) || 1;
        const portions = guests * (servings / totalServings);
        (recipe.ingredients || []).forEach(ing => {
          const invItem = inventory.find(i => i.id === ing.inventory_item_id || i.name === ing.inventory_item_name);
          if (!invItem) return;
          const qty = parseFloat(ing.quantity_needed) || 0;
          const converted = convertQtyToInventoryUnit(qty, ing.unit, invItem.unit);
          consumption[invItem.id] = (consumption[invItem.id] || 0) + converted * portions;
        });
      });
    });

    return inventory
      .filter(item => consumption[item.id] > 0)
      .map(item => ({
        ...item,
        projected_consumption: consumption[item.id],
        stock_after: parseFloat(item.current_quantity) - consumption[item.id],
      }))
      .filter(item => item.stock_after < parseFloat(item.reorder_point))
      .sort((a, b) => a.stock_after - b.stock_after);
  }, [forecastResponse, recipes, inventory]);

  // ────────────────────────────────────────────
  // MUTATIONS
  // ────────────────────────────────────────────
  const createItemMutation = useMutation({
    mutationFn: (d) => base44.entities.InventoryItem.create(d),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['inventory'] }); setIsDialogOpen(false); setEditingItem(null); resetForm(); },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const updateItemMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.InventoryItem.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['inventory'] }); setIsDialogOpen(false); setEditingItem(null); resetForm(); },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const deleteItemMutation = useMutation({
    mutationFn: (id) => base44.entities.InventoryItem.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['inventory'] }); setDeleteItemId(null); },
    onError: (e) => { alert(e?.response?.data?.detail || e.message || 'Delete failed'); setDeleteItemId(null); },
  });

  const createLossMutation = useMutation({
    mutationFn: (d) => base44.entities.ProductionLoss.create(d),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['productionlosses'] });
      setIsLossDialogOpen(false);
      setNewLoss({ inventory_item_name: '', quantity: '', unit: 'kg', date: format(new Date(), 'yyyy-MM-dd'), reason: 'Produktionsfehler', description: '', cost: '', shift: 'Abend', reported_by: '' });
    },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const createSingleOrderMutation = useMutation({
    mutationFn: (d) => base44.entities.Order.create(d),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['orders'] }); setNewOrderDialog(false); setOrderForm(EMPTY_ORDER_FORM); },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const createBulkOrdersMutation = useMutation({
    mutationFn: (d) => base44.entities.Order.bulkCreate(d),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['orders'] }); },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const updateOrderMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Order.update(id, data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['orders'] }); queryClient.invalidateQueries({ queryKey: ['inventory'] }); },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const createSupplierMutation = useMutation({
    mutationFn: (data) => base44.entities.Supplier.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      setIsSupplierDialogOpen(false);
      setEditingSupplier(null);
      setNewSupplier({ name: '', lead_time_days: '' });
    },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const updateSupplierMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Supplier.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setIsSupplierDialogOpen(false);
      setEditingSupplier(null);
      setNewSupplier({ name: '', lead_time_days: '' });
    },
    onError: (e) => alert(e?.response?.data?.detail || e.message),
  });

  const deleteSupplierMutation = useMutation({
    mutationFn: (id) => base44.entities.Supplier.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      setDeleteSupplierId(null);
    },
    onError: (e) => {
      alert(e?.response?.data?.detail || e.message || 'Delete failed');
      setDeleteSupplierId(null);
    },
  });

  // ────────────────────────────────────────────
  // DERIVED DATA
  // ────────────────────────────────────────────
  const lowStockItems = useMemo(() =>
    inventory
      .filter(item => item.current_quantity <= item.reorder_point)
      .sort((a, b) => (a.current_quantity / (a.reorder_point || 1)) - (b.current_quantity / (b.reorder_point || 1))),
    [inventory]
  );

  const criticalItems = useMemo(() =>
    inventory.filter(item => item.current_quantity <= item.minimum_quantity),
    [inventory]
  );

  const hasCritical = criticalItems.length > 0;

  // Arriving strip: group orders by supplier + expected delivery
  const arrivingOrders = useMemo(() => {
    const active = orders.filter(o =>
      o.status === t("orders.statusOrdered") || o.status === t("orders.statusArriving")
    );
    const grouped = {};
    active.forEach(o => {
      const supplierLabel = o.supplier_name || o.supplier || t("orders.unknown");
      const key = `${supplierLabel}::${o.expected_delivery_date || 'none'}`;
      if (!grouped[key]) {
        grouped[key] = { supplier: supplierLabel, expected: o.expected_delivery_date, items: [] };
      }
      grouped[key].items.push(o);
    });
    return Object.values(grouped).sort((a, b) => {
      if (!a.expected) return 1;
      if (!b.expected) return -1;
      return a.expected.localeCompare(b.expected);
    });
  }, [orders, t]);

  // Orders tab filtering
  const filteredOrders = useMemo(() => {
    if (ordersFilter === 'active') {
      return orders.filter(o =>
        o.status === t("orders.statusOrdered") || o.status === t("orders.statusArriving")
      );
    }
    if (ordersFilter === 'suggestions') {
      return orders.filter(o => o.status === t("orders.statusSuggestion"));
    }
    // history
    return orders.filter(o =>
      o.status === t("orders.statusDelivered") || o.status === t("orders.statusCancelled")
    );
  }, [orders, ordersFilter, t]);

  // Products tab: sort critical/low to top
  const filteredInventory = useMemo(() => {
    const getCatValue = (translatedCategory) => {
      const map = {
        [t('inventory.freshIngredients')]: 'Frische Zutaten',
        [t('inventory.dryGoods')]: 'Trockenwaren',
        [t('inventory.beverages')]: 'Getränke',
        [t('inventory.packaging')]: 'Verpackung',
        [t('inventory.other')]: 'Sonstiges',
      };
      return map[translatedCategory] || translatedCategory;
    };
    let items = filterCategory === 'all' ? [...inventory] : inventory.filter(item => item.category === getCatValue(filterCategory));
    // Sort: critical first, then low, then OK
    items.sort((a, b) => {
      const scoreA = a.current_quantity <= a.minimum_quantity ? 0 : a.current_quantity <= a.reorder_point ? 1 : 2;
      const scoreB = b.current_quantity <= b.minimum_quantity ? 0 : b.current_quantity <= b.reorder_point ? 1 : 2;
      return scoreA - scoreB;
    });
    return items;
  }, [inventory, filterCategory, t]);

  const supplierStats = useMemo(() => {
    return suppliers.map((supplier) => ({
      ...supplier,
      productCount: inventory.filter(item => String(item.supplier_id) === String(supplier.id)).length,
      activeOrderCount: orders.filter(order =>
        String(order.supplier_id) === String(supplier.id) &&
        order.status !== t("orders.statusDelivered") &&
        order.status !== t("orders.statusCancelled")
      ).length,
    }));
  }, [inventory, orders, suppliers, t]);

  // Losses weekly summary
  const weekLossSummary = useMemo(() => {
    const now = new Date();
    const weekStart = startOfWeek(now, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
    const weekLosses = losses.filter(l => {
      try {
        const d = new Date(l.date);
        return d >= weekStart && d <= weekEnd;
      } catch { return false; }
    });
    return {
      count: weekLosses.length,
      cost: weekLosses.reduce((s, l) => s + (l.cost || 0), 0),
    };
  }, [losses]);

  // ────────────────────────────────────────────
  // HANDLERS
  // ────────────────────────────────────────────
  const resetForm = () => {
    setNewItem(normalizeInventoryItemForm());
  };

  const resetSupplierForm = () => {
    setNewSupplier({ name: '', lead_time_days: '' });
  };

  const openSupplierManager = () => {
    setIsDialogOpen(false);
    setNewOrderDialog(false);
    handleTabChange('suppliers');
  };

  const handleEditSupplier = (supplier) => {
    setEditingSupplier(supplier);
    setNewSupplier({
      name: supplier.name || '',
      lead_time_days: supplier.lead_time_days != null ? String(supplier.lead_time_days) : '',
    });
    setIsSupplierDialogOpen(true);
  };

  const handleSupplierSubmit = (e) => {
    e.preventDefault();
    const payload = {
      name: newSupplier.name.trim(),
      lead_time_days: newSupplier.lead_time_days !== '' ? Number(newSupplier.lead_time_days) : null,
    };
    if (editingSupplier) {
      updateSupplierMutation.mutate({ id: editingSupplier.id, data: payload });
      return;
    }
    createSupplierMutation.mutate(payload);
  };

  const handleItemSupplierSelect = (value) => {
    if (value === 'none') {
      setNewItem((item) => ({ ...item, supplier_id: '', supplier: '' }));
      return;
    }
    const selectedSupplier = suppliers.find((supplier) => String(supplier.id) === String(value));
    if (!selectedSupplier) return;
    setNewItem((item) => ({
      ...item,
      supplier_id: String(selectedSupplier.id),
      supplier: selectedSupplier.name,
      lead_time_days:
        selectedSupplier.lead_time_days != null
          ? String(selectedSupplier.lead_time_days)
          : item.lead_time_days,
    }));
  };

  const handleOrderSupplierSelect = (value) => {
    if (value === 'none') {
      setOrderForm((form) => ({ ...form, supplier_id: '', supplier: '' }));
      return;
    }
    const selectedSupplier = suppliers.find((supplier) => String(supplier.id) === String(value));
    if (!selectedSupplier) return;
    setOrderForm((form) => ({
      ...form,
      supplier_id: String(selectedSupplier.id),
      supplier: selectedSupplier.name,
      expected_delivery_date:
        getExpectedDeliveryDate(form.order_date, selectedSupplier.lead_time_days) || form.expected_delivery_date,
    }));
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    const matchedSupplier = suppliers.find((supplier) => supplier.name === (item.supplier_name || item.supplier));
    setNewItem(normalizeInventoryItemForm({
      ...item,
      supplier_id: item.supplier_id || matchedSupplier?.id,
    }));
    setIsDialogOpen(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const itemData = buildInventoryItemPayload(newItem);
    if (editingItem) {
      updateItemMutation.mutate({ id: editingItem.id, data: itemData });
    } else {
      createItemMutation.mutate(itemData);
    }
  };

  const handleLossSubmit = (e) => {
    e.preventDefault();
    createLossMutation.mutate({
      ...newLoss,
      quantity: parseFloat(newLoss.quantity),
      cost: parseFloat(newLoss.cost),
    });
  };

  // ─── One-click order from Attention Strip ───
  const handleQuickOrder = (item) => {
    const deficit = item.reorder_point - item.current_quantity;
    const buffer = (item.reorder_point - item.minimum_quantity) * 0.5;
    const plan = getPurchasePlan(item, Math.ceil(deficit + buffer));
    const today = new Date();
    const expected = addDays(today, item.lead_time_days || 2);

    createSingleOrderMutation.mutate({
      inventory_item_id: item.id,
      inventory_item_name: item.name,
      article_number: item.article_number || '',
      purchase_units_ordered: plan.hasPurchaseConfig ? plan.purchaseUnitsNeeded : null,
      package_label: plan.packageLabel || getPurchaseUnitLabel(item),
      suggested_quantity: plan.coverageQuantity,
      unit: item.unit,
      supplier_id: item.supplier_id || null,
      supplier: item.supplier_name || item.supplier || '',
      order_date: format(today, 'yyyy-MM-dd'),
      expected_delivery_date: format(expected, 'yyyy-MM-dd'),
      status: t("orders.statusOrdered"),
      estimated_cost: Number(plan.estimatedCost.toFixed(2)),
      notes: plan.hasPurchaseConfig
        ? `${t("orders.automaticallyGenerated", { current: item.current_quantity, unit: item.unit, reorder: item.reorder_point })} ${plan.summary}`
        : t("orders.automaticallyGenerated", { current: item.current_quantity, unit: item.unit, reorder: item.reorder_point }),
    });
  };

  // ─── Order All from Attention Strip ───
  const handleOrderAll = () => {
    const ordersToCreate = lowStockItems.filter(item => item.supplier_id || item.supplier).map(item => {
      const deficit = item.reorder_point - item.current_quantity;
      const buffer = (item.reorder_point - item.minimum_quantity) * 0.5;
      const plan = getPurchasePlan(item, Math.ceil(deficit + buffer));
      const today = new Date();
      const expected = addDays(today, item.lead_time_days || 2);
      return {
        inventory_item_id: item.id,
        inventory_item_name: item.name,
        article_number: item.article_number || '',
        purchase_units_ordered: plan.hasPurchaseConfig ? plan.purchaseUnitsNeeded : null,
        package_label: plan.packageLabel || getPurchaseUnitLabel(item),
        suggested_quantity: plan.coverageQuantity,
        unit: item.unit,
        supplier_id: item.supplier_id || null,
        supplier: item.supplier_name || item.supplier || '',
        order_date: format(today, 'yyyy-MM-dd'),
        expected_delivery_date: format(expected, 'yyyy-MM-dd'),
        status: t("orders.statusOrdered"),
        estimated_cost: Number(plan.estimatedCost.toFixed(2)),
        notes: plan.hasPurchaseConfig
          ? `${t("orders.automaticallyGenerated", { current: item.current_quantity, unit: item.unit, reorder: item.reorder_point })} ${plan.summary}`
          : t("orders.automaticallyGenerated", { current: item.current_quantity, unit: item.unit, reorder: item.reorder_point }),
      };
    });
    if (ordersToCreate.length > 0) {
      createBulkOrdersMutation.mutate(ordersToCreate);
    }
  };

  // ─── One-click Received on Arriving Strip ───
  const handleReceiveSupplier = async (group) => {
    for (const order of group.items) {
      await updateOrderMutation.mutateAsync({
        id: order.id,
        data: {
          status: t("orders.statusDelivered"),
          actual_delivery_date: format(new Date(), 'yyyy-MM-dd'),
        },
      });
    }
    // Switch to orders tab → history so user sees the delivered order
    setOrdersFilter('history');
    handleTabChange('orders');
  };

  // ─── Order status actions ───
  const handleMarkOrdered = (order) => {
    updateOrderMutation.mutate({ id: order.id, data: { status: t("orders.statusOrdered") } });
  };
  const handleMarkArriving = (order) => {
    updateOrderMutation.mutate({ id: order.id, data: { status: t("orders.statusArriving") } });
  };
  const handleMarkDelivered = (order) => {
    updateOrderMutation.mutate(
      { id: order.id, data: { status: t("orders.statusDelivered"), actual_delivery_date: format(new Date(), 'yyyy-MM-dd') } },
      { onSuccess: () => setOrdersFilter('history') }
    );
  };
  const handleCancelOrder = (order) => {
    updateOrderMutation.mutate({ id: order.id, data: { status: t("orders.statusCancelled") } });
  };

  // ─── New Order dialog handlers ───
  const handleInventoryItemSelect = (itemId) => {
    if (itemId === 'manual') {
      setOrderForm(f => ({
        ...f,
        inventory_item_id: '',
        inventory_item_name: '',
        article_number: '',
        package_label: '',
        unit: '',
      }));
      return;
    }
    const item = inventory.find(i => String(i.id) === String(itemId));
    if (item) {
      const matchedSupplier = suppliers.find((supplier) => supplier.name === (item.supplier_name || item.supplier));
      const orderDraft = orderForm.suggested_quantity ? getOrderDraft(item, parseFloat(orderForm.suggested_quantity)) : null;
      setOrderForm(f => ({
        ...f,
        inventory_item_id: item.id,
        inventory_item_name: item.name,
        article_number: item.article_number || f.article_number,
        package_label: getPurchaseUnitLabel(item) || f.package_label,
        unit: item.unit,
        supplier_id: item.supplier_id ? String(item.supplier_id) : matchedSupplier ? String(matchedSupplier.id) : f.supplier_id,
        supplier: item.supplier_name || item.supplier || f.supplier,
        expected_delivery_date:
          getExpectedDeliveryDate(f.order_date, item.lead_time_days) || f.expected_delivery_date,
        estimated_cost: orderDraft ? orderDraft.estimatedCost.toFixed(2) : f.estimated_cost,
      }));
    }
  };

  const handleSubmitNewOrder = () => {
    const finalQuantity = selectedOrderDraft
      ? selectedOrderDraft.coverageQuantity
      : parseFloat(orderForm.suggested_quantity) || 0;
    const payload = {
      inventory_item_name: orderForm.inventory_item_name,
      article_number: orderForm.article_number,
      purchase_units_ordered: selectedOrderDraft?.usesPurchaseUnits
        ? Number(selectedOrderDraft.purchaseUnitsOrdered)
        : null,
      package_label: orderForm.package_label,
      suggested_quantity: finalQuantity,
      unit: orderForm.unit,
      supplier_id: orderForm.supplier_id || null,
      supplier: orderForm.supplier,
      order_date: orderForm.order_date,
      expected_delivery_date: orderForm.expected_delivery_date || null,
      estimated_cost: selectedOrderDraft ? Number(selectedOrderDraft.estimatedCost.toFixed(2)) : parseFloat(orderForm.estimated_cost) || 0,
      status: orderForm.status || t("orders.statusOrdered"),
      notes: selectedOrderDraft?.usesPurchaseUnits && !orderForm.notes
        ? selectedOrderDraft.summary
        : orderForm.notes,
    };
    if (orderForm.inventory_item_id) payload.inventory_item_id = orderForm.inventory_item_id;
    createSingleOrderMutation.mutate(payload);
  };

  const isFormValid = orderForm.inventory_item_name && orderForm.suggested_quantity && orderForm.unit;
  const selectedOrderItem = inventory.find(i => String(i.id) === String(orderForm.inventory_item_id));
  const selectedOrderDraft = selectedOrderItem && orderForm.suggested_quantity
    ? getOrderDraft(selectedOrderItem, parseFloat(orderForm.suggested_quantity))
    : null;

  // ─── Status helpers ───
  const getStockStatus = (item) => {
    if (item.current_quantity <= item.minimum_quantity) return { label: t('inventory.critical'), color: 'bg-red-100 text-red-700 border-red-200' };
    if (item.current_quantity <= item.reorder_point) return { label: t('inventory.low'), color: 'bg-orange-100 text-orange-700 border-orange-200' };
    return { label: t('inventory.ok'), color: 'bg-green-100 text-green-700 border-green-200' };
  };

  const getStatusColor = (status) => {
    if (status === t("orders.statusSuggestion")) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (status === t("orders.statusOrdered")) return 'bg-orange-100 text-orange-700 border-orange-200';
    if (status === t("orders.statusArriving")) return 'bg-teal-100 text-teal-700 border-teal-200';
    if (status === t("orders.statusDelivered")) return 'bg-green-100 text-green-700 border-green-200';
    if (status === t("orders.statusCancelled")) return 'bg-slate-100 text-slate-700 border-slate-200';
    return 'bg-slate-100 text-slate-700 border-slate-200';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return format(new Date(dateStr), 'dd.MM.yyyy'); } catch { return '-'; }
  };

  const isDeliveryToday = (dateStr) => {
    if (!dateStr) return false;
    try { return isToday(parseISO(dateStr)); } catch { return false; }
  };

  const reasonColors = {
    'Kaputt': 'bg-red-100 text-red-700', 'Verdorben': 'bg-orange-100 text-orange-700',
    'Produktionsfehler': 'bg-yellow-100 text-yellow-700', 'Verschüttet': 'bg-blue-100 text-blue-700',
    'Verbrannt': 'bg-purple-100 text-purple-700', 'Abgelaufen': 'bg-pink-100 text-pink-700',
    'Qualitätsmangel': 'bg-indigo-100 text-indigo-700', 'Sonstiges': 'bg-slate-100 text-slate-700',
  };

  const getReasonLabel = (reason) => {
    const map = {
      'Kaputt': t('inventory.broken'), 'Verdorben': t('inventory.spoiled'),
      'Produktionsfehler': t('inventory.productionError'), 'Verschüttet': t('inventory.spilled'),
      'Verbrannt': t('inventory.burned'), 'Abgelaufen': t('inventory.expired'),
      'Qualitätsmangel': t('inventory.qualityIssue'), 'Sonstiges': t('inventory.otherReason'),
    };
    return map[reason] || reason;
  };

  // ────────────────────────────────────────────
  // RENDER
  // ────────────────────────────────────────────
  return (
    <Layout>
      <div className="p-6 md:p-8 min-h-screen">
        <div className="max-w-7xl mx-auto">

          {/* ═══ HEADER ═══ */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">{t('stock.title')}</h1>
              <p className="text-slate-500 mt-1">{t('stock.subtitle')}</p>
            </div>
            <div className="flex gap-3 flex-wrap">
              {/* Report Loss */}
              <Dialog open={isLossDialogOpen} onOpenChange={setIsLossDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="shadow-md border-red-200 text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4 mr-2" />
                    {t('inventory.reportLoss')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                      {t('inventory.reportProductionLoss')}
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleLossSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="loss_item">{t('inventory.lossProduct')}</Label>
                        <Select value={newLoss.inventory_item_name} onValueChange={(value) => {
                          const item = inventory.find(i => i.name === value);
                          setNewLoss({ ...newLoss, inventory_item_name: value, inventory_item_id: item?.id, unit: item?.unit || 'kg', cost: item?.cost_per_unit ? (parseFloat(newLoss.quantity || 0) * item.cost_per_unit).toFixed(2) : '' });
                        }}>
                          <SelectTrigger><SelectValue placeholder={t('inventory.selectProduct')} /></SelectTrigger>
                          <SelectContent>
                            {inventory.map(item => (<SelectItem key={item.id} value={item.name}>{item.name}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="loss_reason">{t('inventory.reason')}</Label>
                        <Select value={newLoss.reason} onValueChange={(v) => setNewLoss({ ...newLoss, reason: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Kaputt">{t('inventory.broken')}</SelectItem>
                            <SelectItem value="Verdorben">{t('inventory.spoiled')}</SelectItem>
                            <SelectItem value="Produktionsfehler">{t('inventory.productionError')}</SelectItem>
                            <SelectItem value="Verschüttet">{t('inventory.spilled')}</SelectItem>
                            <SelectItem value="Verbrannt">{t('inventory.burned')}</SelectItem>
                            <SelectItem value="Abgelaufen">{t('inventory.expired')}</SelectItem>
                            <SelectItem value="Qualitätsmangel">{t('inventory.qualityIssue')}</SelectItem>
                            <SelectItem value="Sonstiges">{t('inventory.otherReason')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="loss_quantity">{t('inventory.quantity')}</Label>
                        <Input id="loss_quantity" type="number" step="0.01" value={newLoss.quantity} onChange={(e) => {
                          const item = inventory.find(i => i.name === newLoss.inventory_item_name);
                          setNewLoss({ ...newLoss, quantity: e.target.value, cost: item?.cost_per_unit ? (parseFloat(e.target.value || 0) * item.cost_per_unit).toFixed(2) : '' });
                        }} required />
                      </div>
                      <div>
                        <Label htmlFor="loss_unit">{t('inventory.unit')}</Label>
                        <Input id="loss_unit" value={newLoss.unit} disabled />
                      </div>
                      <div>
                        <Label htmlFor="loss_date">{t('inventory.date')}</Label>
                        <Input id="loss_date" type="date" value={newLoss.date} onChange={(e) => setNewLoss({ ...newLoss, date: e.target.value })} required />
                      </div>
                      <div>
                        <Label htmlFor="loss_shift">{t('inventory.shift')}</Label>
                        <Select value={newLoss.shift} onValueChange={(v) => setNewLoss({ ...newLoss, shift: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Mittag">{t('inventory.lunch')}</SelectItem>
                            <SelectItem value="Abend">{t('inventory.dinner')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="loss_cost">{t('inventory.cost')}</Label>
                        <Input id="loss_cost" type="number" step="0.01" value={newLoss.cost} onChange={(e) => setNewLoss({ ...newLoss, cost: e.target.value })} />
                      </div>
                      <div>
                        <Label htmlFor="loss_reported">{t('inventory.reportedBy')}</Label>
                        <Input id="loss_reported" value={newLoss.reported_by} onChange={(e) => setNewLoss({ ...newLoss, reported_by: e.target.value })} placeholder={t('inventory.name')} />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="loss_description">{t('inventory.description')}</Label>
                      <Textarea id="loss_description" value={newLoss.description} onChange={(e) => setNewLoss({ ...newLoss, description: e.target.value })} placeholder={t('inventory.whatHappened')} rows={3} />
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsLossDialogOpen(false)}>{t('inventory.cancel')}</Button>
                      <Button type="submit" className="bg-red-600 hover:bg-red-700">{t('inventory.reportLoss')}</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* Add Product */}
              <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) { setEditingItem(null); resetForm(); } }}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="shadow-md">
                    <Plus className="w-4 h-4 mr-2" />
                    {t('inventory.addProduct')}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingItem ? t('inventory.editProduct') : t('inventory.newProduct')}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="name">{t('inventory.productName')}</Label>
                        <Input id="name" value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })} required />
                      </div>
                      <div>
                        <Label htmlFor="article_number">{t('inventory.articleNumber')}</Label>
                        <Input
                          id="article_number"
                          value={newItem.article_number}
                          onChange={(e) => setNewItem({ ...newItem, article_number: e.target.value })}
                          placeholder={t('inventory.articleNumberPlaceholder')}
                        />
                      </div>
                      <div>
                        <Label htmlFor="category">{t('inventory.category')}</Label>
                        <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Frische Zutaten">{t('inventory.freshIngredients')}</SelectItem>
                            <SelectItem value="Trockenwaren">{t('inventory.dryGoods')}</SelectItem>
                            <SelectItem value="Getränke">{t('inventory.beverages')}</SelectItem>
                            <SelectItem value="Verpackung">{t('inventory.packaging')}</SelectItem>
                            <SelectItem value="Sonstiges">{t('inventory.other')}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="current_quantity">{t('inventory.currentStock')}</Label>
                        <Input id="current_quantity" type="number" step="0.01" value={newItem.current_quantity} onChange={(e) => setNewItem({ ...newItem, current_quantity: e.target.value })} required />
                      </div>
                      <div>
                        <Label htmlFor="unit">{t('inventory.unit')}</Label>
                        <Select value={newItem.unit} onValueChange={(v) => setNewItem({ ...newItem, unit: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kg">kg</SelectItem>
                            <SelectItem value="Liter">Liter</SelectItem>
                            <SelectItem value="Stück">Stück</SelectItem>
                            <SelectItem value="Packung">Packung</SelectItem>
                            <SelectItem value="Gramm">Gramm</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="purchase_unit_quantity">{t('inventory.purchaseUnitSize')}</Label>
                        <Input
                          id="purchase_unit_quantity"
                          type="number"
                          min="0"
                          step="0.01"
                          value={newItem.purchase_unit_quantity}
                          onChange={(e) => setNewItem({ ...newItem, purchase_unit_quantity: e.target.value })}
                          placeholder={t('inventory.purchaseUnitSizePlaceholder')}
                        />
                        <p className="text-xs text-slate-500 mt-1">
                          {t('inventory.purchaseUnitSizeDesc', {
                            size: newItem.purchase_unit_quantity || "1",
                            unit: newItem.unit || "kg",
                          })}
                        </p>
                      </div>
                      <div>
                        <Label htmlFor="purchase_unit_name">{t('inventory.packageType')}</Label>
                        <Input
                          id="purchase_unit_name"
                          value={newItem.purchase_unit_name}
                          onChange={(e) => setNewItem({ ...newItem, purchase_unit_name: e.target.value })}
                          placeholder={t('inventory.packageTypePlaceholder')}
                        />
                      </div>
                      <div>
                        <Label htmlFor="purchase_unit_cost">{t('inventory.purchaseUnitCost')}</Label>
                        <Input
                          id="purchase_unit_cost"
                          type="number"
                          min="0"
                          step="0.01"
                          value={newItem.purchase_unit_cost}
                          onChange={(e) => setNewItem({ ...newItem, purchase_unit_cost: e.target.value })}
                          placeholder="15.74"
                        />
                        {getDerivedCostPerUnit(newItem) !== null && (
                          <p className="text-xs text-slate-500 mt-1">
                            {t('inventory.recipeCostHint', {
                              cost: formatInventoryNumber(getDerivedCostPerUnit(newItem)),
                              unit: newItem.unit || "kg",
                            })}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="minimum_quantity">{t('inventory.minimumQuantity')}</Label>
                        <Input id="minimum_quantity" type="number" step="0.01" value={newItem.minimum_quantity} onChange={(e) => setNewItem({ ...newItem, minimum_quantity: e.target.value })} required />
                      </div>
                      <div>
                        <Label htmlFor="reorder_point">{t('inventory.reorderPoint')}</Label>
                        <Input id="reorder_point" type="number" step="0.01" value={newItem.reorder_point} onChange={(e) => setNewItem({ ...newItem, reorder_point: e.target.value })} required />
                      </div>
                      <div>
                        <Label htmlFor="supplier">{t('inventory.supplier')}</Label>
                        <Select value={newItem.supplier_id || 'none'} onValueChange={handleItemSupplierSelect}>
                          <SelectTrigger id="supplier">
                            <SelectValue placeholder={t('suppliers.selectSupplier')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">{t('suppliers.noSupplier')}</SelectItem>
                            {suppliers.map((supplier) => (
                              <SelectItem key={supplier.id} value={String(supplier.id)}>
                                {supplier.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <p className="text-xs text-slate-500">{t('suppliers.assignSupplierHint')}</p>
                          <Button type="button" variant="ghost" size="sm" className="h-auto px-0 text-xs text-blue-600 hover:text-blue-700" onClick={openSupplierManager}>
                            {t('suppliers.manageSuppliers')}
                          </Button>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="lead_time_days">{t('inventory.leadTimeDays')}</Label>
                        <Input id="lead_time_days" type="number" value={newItem.lead_time_days} onChange={(e) => setNewItem({ ...newItem, lead_time_days: e.target.value })} />
                      </div>
                    </div>
                    <div className="flex justify-end gap-3 pt-4">
                      <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>{t('inventory.cancel')}</Button>
                      <Button type="submit" className="bg-blue-600 hover:bg-blue-700">{editingItem ? t('inventory.update') : t('inventory.save')}</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              {/* New Order */}
              <Button className="bg-blue-600 hover:bg-blue-700 shadow-md" onClick={() => { setOrderForm(EMPTY_ORDER_FORM); setNewOrderDialog(true); }}>
                <Plus className="w-4 h-4 mr-2" />
                {t('orders.newOrder')}
              </Button>
            </div>
          </div>

          {/* Delete Item Confirmation */}
          <AlertDialog open={!!deleteItemId} onOpenChange={() => setDeleteItemId(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('inventory.deleteProduct')}</AlertDialogTitle>
                <AlertDialogDescription>{t('inventory.deleteProductDesc')}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('inventory.cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteItemMutation.mutate(deleteItemId)} className="bg-red-600 hover:bg-red-700">{t('inventory.delete')}</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog open={!!deleteSupplierId} onOpenChange={() => setDeleteSupplierId(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('suppliers.deleteSupplier')}</AlertDialogTitle>
                <AlertDialogDescription>{t('suppliers.deleteSupplierDesc')}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('inventory.cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={() => deleteSupplierMutation.mutate(deleteSupplierId)} className="bg-red-600 hover:bg-red-700">
                  {t('inventory.delete')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* ═══ ZONE 1: ATTENTION STRIP ═══ */}
          {lowStockItems.length === 0 ? (
            <div className="mb-4 px-4 py-3 rounded-lg bg-green-50 border border-green-200 flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium text-green-700">{t('stock.allStockOk')}</span>
            </div>
          ) : (
            <div className={`mb-4 rounded-lg border ${hasCritical ? 'bg-red-50 border-red-200' : 'bg-orange-50 border-orange-200'}`}>
              <div className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <AlertTriangle className={`w-4 h-4 ${hasCritical ? 'text-red-600' : 'text-orange-600'}`} />
                  <span className={`text-sm font-semibold ${hasCritical ? 'text-red-700' : 'text-orange-700'}`}>
                    {t('stock.itemsBelowReorder', { count: lowStockItems.length })}
                  </span>
                </div>
                <Button
                  size="sm"
                  onClick={handleOrderAll}
                  disabled={createBulkOrdersMutation.isPending}
                  className="bg-blue-600 hover:bg-blue-700 text-xs"
                >
                  <ShoppingBag className="w-3.5 h-3.5 mr-1" />
                  {t('stock.orderAll')}
                </Button>
              </div>
              <div className="px-4 pb-3 space-y-2">
                {lowStockItems.map(item => {
                  const isCritical = item.current_quantity <= item.minimum_quantity;
                  const purchasePlan = getPurchasePlan(
                    item,
                    Math.ceil((item.reorder_point - item.current_quantity) + ((item.reorder_point - item.minimum_quantity) * 0.5)),
                  );
                  return (
                    <div key={item.id} className={`flex items-center justify-between py-2 px-3 rounded-md ${isCritical ? 'bg-red-100/60' : 'bg-white/60'}`}>
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="min-w-0 w-40">
                          <span className="font-medium text-sm text-slate-900 truncate block">{item.name}</span>
                          {purchasePlan.hasPurchaseConfig && (
                            <span className="text-xs text-slate-500">
                              {purchasePlan.summary}
                            </span>
                          )}
                        </div>
                        {isCritical && <Badge className="bg-red-100 text-red-700 border-red-200 border text-xs">{t('inventory.critical')}</Badge>}
                        <StockBar current={item.current_quantity} reorder={item.reorder_point} unit={item.unit} />
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleQuickOrder(item)}
                        disabled={createSingleOrderMutation.isPending}
                        className="ml-3 text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                      >
                        {t('stock.order')}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ═══ ZONE 2: ARRIVING STRIP ═══ */}
          {arrivingOrders.length > 0 && (
            <div className="mb-6 rounded-lg border border-teal-200 bg-teal-50/50">
              <div className="px-4 py-3 flex items-center gap-2 border-b border-teal-100">
                <Truck className="w-4 h-4 text-teal-600" />
                <span className="text-sm font-semibold text-teal-700">{t('stock.arriving')}</span>
              </div>
              <div className="px-4 py-2 space-y-2">
                {arrivingOrders.map((group, idx) => {
                  const today = isDeliveryToday(group.expected);
                  return (
                    <div key={idx} className={`flex items-center justify-between py-2 px-3 rounded-md ${today ? 'bg-teal-100' : 'bg-white/60'}`}>
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <span className="font-medium text-sm text-slate-900">{group.supplier}</span>
                        <span className="text-xs text-slate-500">{group.items.length} {t('stock.items')}</span>
                        <span className={`text-xs ${today ? 'font-semibold text-teal-700' : 'text-slate-500'}`}>
                          {today ? t('stock.arrivingToday') : formatDate(group.expected)}
                        </span>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleReceiveSupplier(group)}
                        disabled={updateOrderMutation.isPending}
                        className="ml-3 text-xs bg-teal-600 hover:bg-teal-700"
                      >
                        <CheckCircle className="w-3.5 h-3.5 mr-1" />
                        {t('stock.received')}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ═══ ZONE 3: TABS ═══ */}
          <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
            <TabsList className="bg-white shadow-sm">
              <TabsTrigger value="products">
                <Package className="w-4 h-4 mr-2" />
                {t('stock.productsTab')}
              </TabsTrigger>
              <TabsTrigger value="orders">
                <ShoppingBag className="w-4 h-4 mr-2" />
                {t('stock.ordersTab')}
              </TabsTrigger>
              <TabsTrigger value="suppliers">
                <Building2 className="w-4 h-4 mr-2" />
                {t('stock.suppliersTab')}
              </TabsTrigger>
              <TabsTrigger value="losses">
                <Trash2 className="w-4 h-4 mr-2" />
                {t('stock.lossesTab')}
              </TabsTrigger>
            </TabsList>

            {/* ─── TAB: PRODUCTS ─── */}
            <TabsContent value="products">
              <div className="mb-4">
                <Select value={filterCategory} onValueChange={setFilterCategory}>
                  <SelectTrigger className="w-64 bg-white shadow-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('inventory.allCategories')}</SelectItem>
                    <SelectItem value="Frische Zutaten">{t('inventory.freshIngredients')}</SelectItem>
                    <SelectItem value="Trockenwaren">{t('inventory.dryGoods')}</SelectItem>
                    <SelectItem value="Getränke">{t('inventory.beverages')}</SelectItem>
                    <SelectItem value="Verpackung">{t('inventory.packaging')}</SelectItem>
                    <SelectItem value="Sonstiges">{t('inventory.other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* ─── Forecast depletion alerts ─── */}
              {stockAlerts.length > 0 && (
                <div className="mb-4 border border-amber-200 bg-amber-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <TrendingDown className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-semibold text-amber-800">
                      {stockAlerts.length} item{stockAlerts.length > 1 ? 's' : ''} will fall below reorder point in 7 days
                    </span>
                  </div>
                  <div className="space-y-2">
                    {stockAlerts.map(item => (
                      <div key={item.id} className="flex items-center justify-between text-sm bg-white rounded-md px-3 py-2 border border-amber-100">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                          <span className="font-medium text-slate-700">{item.name}</span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-slate-500">
                          <span>Now: <span className="font-medium text-slate-700">{parseFloat(item.current_quantity).toFixed(1)} {item.unit}</span></span>
                          <span>−{item.projected_consumption.toFixed(1)} {item.unit}</span>
                          <span className={`font-semibold ${item.stock_after < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                            After: {item.stock_after.toFixed(1)} {item.unit}
                          </span>
                          <span className="text-slate-400">Reorder at: {parseFloat(item.reorder_point).toFixed(1)} {item.unit}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-amber-600 mt-2 opacity-70">Based on 7-day guest forecast × recipe mix</p>
                </div>
              )}

              <Card className="border-0 shadow-md">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>{t('inventory.product')}</TableHead>
                          <TableHead>{t('inventory.stock')}</TableHead>
                          <TableHead>{t('inventory.category')}</TableHead>
                          <TableHead>{t('inventory.supplierCol')}</TableHead>
                          <TableHead>{t('inventory.costPerUnit')}</TableHead>
                          <TableHead className="text-right">{t('inventory.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {isLoading ? (
                          <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">{t('inventory.loading')}</TableCell></TableRow>
                        ) : filteredInventory.length === 0 ? (
                          <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">{t('inventory.noProducts')}</TableCell></TableRow>
                        ) : (
                          filteredInventory.map((item) => {
                            const status = getStockStatus(item);
                            return (
                              <TableRow key={item.id} className="hover:bg-slate-50">
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <div>
                                      <div className="font-medium">{item.name}</div>
                                      {item.article_number && (
                                        <div className="text-xs text-slate-400">{t('inventory.articleNumber')}: {item.article_number}</div>
                                      )}
                                    </div>
                                    <Badge className={`${status.color} border text-xs`}>{status.label}</Badge>
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <StockBar current={item.current_quantity} reorder={item.reorder_point} unit={item.unit} />
                                </TableCell>
                                <TableCell>
                                  <span className="text-sm text-slate-600">{t(`inventory.categories.${item.category}`, item.category)}</span>
                                </TableCell>
                                <TableCell className="text-slate-600">{item.supplier_name || item.supplier || '-'}</TableCell>
                                <TableCell className="text-slate-600">
                                  {getDerivedCostPerUnit(item) !== null ? (
                                    <div>
                                      <div>{formatInventoryNumber(getDerivedCostPerUnit(item))} / {item.unit}</div>
                                      {item.purchase_unit_quantity && (
                                        <div className="text-xs text-slate-400">
                                          {formatInventoryNumber(item.purchase_unit_quantity)} {item.unit}
                                          {item.purchase_unit_cost ? ` • €${formatInventoryNumber(item.purchase_unit_cost)}` : ''}
                                        </div>
                                      )}
                                    </div>
                                  ) : '-'}
                                </TableCell>
                                <TableCell className="text-right">
                                  <div className="flex justify-end gap-1">
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50" onClick={() => handleEdit(item)}>
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setDeleteItemId(item.id)}>
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ─── TAB: ORDERS ─── */}
            <TabsContent value="orders">
              {/* Filter pills */}
              <div className="flex gap-2 mb-4">
                {['active', 'suggestions', 'history'].map(f => (
                  <button
                    key={f}
                    onClick={() => setOrdersFilter(f)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      ordersFilter === f
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    {t(`stock.filter${f.charAt(0).toUpperCase() + f.slice(1)}`)}
                  </button>
                ))}
              </div>

              <Card className="border-0 shadow-md">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>{t("orders.product")}</TableHead>
                          <TableHead>{t("orders.articleNumber")}</TableHead>
                          <TableHead>{t("orders.quantity")}</TableHead>
                          <TableHead>{t("orders.supplier")}</TableHead>
                          <TableHead>{t("orders.orderedOn")}</TableHead>
                          <TableHead>{t("orders.delivery")}</TableHead>
                          <TableHead>{t("orders.status")}</TableHead>
                          <TableHead className="text-right">{t("orders.action")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loadingOrders ? (
                          <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-500">{t('inventory.loading')}</TableCell></TableRow>
                        ) : filteredOrders.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center py-12">
                              <ShoppingBag className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                              <p className="text-slate-500 text-sm">{t('stock.noOrdersInFilter')}</p>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredOrders.map((order) => (
                            <TableRow key={order.id} className="hover:bg-slate-50">
                              <TableCell className="font-medium">{order.inventory_item_name}</TableCell>
                              <TableCell className="text-slate-600">{order.article_number || '-'}</TableCell>
                              <TableCell>
                                {(() => {
                                  if (order.purchase_units_ordered && order.package_label) {
                                    return (
                                      <div>
                                        <div className="font-semibold">
                                          {formatInventoryNumber(order.purchase_units_ordered)} x {order.package_label}
                                        </div>
                                        <div className="text-xs text-slate-500">
                                          {formatInventoryNumber(order.suggested_quantity)} {order.unit}
                                        </div>
                                      </div>
                                    );
                                  }

                                  return (
                                    <span className="font-semibold">
                                      {formatInventoryNumber(order.suggested_quantity)} {order.unit}
                                    </span>
                                  );
                                })()}
                              </TableCell>
                              <TableCell className="text-slate-600">{order.supplier_name || order.supplier || '-'}</TableCell>
                              <TableCell className="text-sm text-slate-600">{formatDate(order.order_date)}</TableCell>
                              <TableCell className="text-sm text-slate-600">{formatDate(order.expected_delivery_date)}</TableCell>
                              <TableCell>
                                <Badge className={`${getStatusColor(order.status)} border`}>{order.status}</Badge>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  {/* Suggestion: Mark Ordered, Cancel */}
                                  {order.status === t("orders.statusSuggestion") && (
                                    <>
                                      <Button size="sm" variant="outline" onClick={() => handleMarkOrdered(order)} className="text-xs text-orange-600 border-orange-200 hover:bg-orange-50">
                                        {t("orders.markAsOrdered")}
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => handleCancelOrder(order)} className="text-xs text-red-600 border-red-200 hover:bg-red-50">
                                        {t("orders.cancel")}
                                      </Button>
                                    </>
                                  )}
                                  {/* Ordered: Mark Arriving, Mark Delivered, Cancel */}
                                  {order.status === t("orders.statusOrdered") && (
                                    <>
                                      <Button size="sm" variant="outline" onClick={() => handleMarkArriving(order)} className="text-xs text-teal-600 border-teal-200 hover:bg-teal-50">
                                        {t("orders.markArriving")}
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => handleMarkDelivered(order)} className="text-xs text-green-600 border-green-200 hover:bg-green-50">
                                        {t("orders.markAsDelivered")}
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => handleCancelOrder(order)} className="text-xs text-red-600 border-red-200 hover:bg-red-50">
                                        {t("orders.cancel")}
                                      </Button>
                                    </>
                                  )}
                                  {/* Arriving: Mark Delivered, Cancel */}
                                  {order.status === t("orders.statusArriving") && (
                                    <>
                                      <Button size="sm" variant="outline" onClick={() => handleMarkDelivered(order)} className="text-xs text-green-600 border-green-200 hover:bg-green-50">
                                        {t("orders.markAsDelivered")}
                                      </Button>
                                      <Button size="sm" variant="outline" onClick={() => handleCancelOrder(order)} className="text-xs text-red-600 border-red-200 hover:bg-red-50">
                                        {t("orders.cancel")}
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ─── TAB: SUPPLIERS ─── */}
            <TabsContent value="suppliers">
              <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-slate-900">{t('suppliers.title')}</h2>
                  <p className="text-sm text-slate-500">{t('suppliers.subtitle')}</p>
                </div>
                <Dialog
                  open={isSupplierDialogOpen}
                  onOpenChange={(open) => {
                    setIsSupplierDialogOpen(open);
                    if (!open) {
                      setEditingSupplier(null);
                      resetSupplierForm();
                    }
                  }}
                >
                  <DialogTrigger asChild>
                    <Button className="bg-blue-600 hover:bg-blue-700 shadow-md">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('suppliers.addSupplier')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>{editingSupplier ? t('suppliers.editSupplier') : t('suppliers.newSupplier')}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSupplierSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="supplier_name">{t('suppliers.name')}</Label>
                        <Input
                          id="supplier_name"
                          value={newSupplier.name}
                          onChange={(e) => setNewSupplier((supplier) => ({ ...supplier, name: e.target.value }))}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="supplier_lead_time">{t('suppliers.leadTimeDays')}</Label>
                        <Input
                          id="supplier_lead_time"
                          type="number"
                          min="0"
                          value={newSupplier.lead_time_days}
                          onChange={(e) => setNewSupplier((supplier) => ({ ...supplier, lead_time_days: e.target.value }))}
                        />
                      </div>
                      <div className="flex justify-end gap-3 pt-2">
                        <Button type="button" variant="outline" onClick={() => setIsSupplierDialogOpen(false)}>
                          {t('inventory.cancel')}
                        </Button>
                        <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                          {editingSupplier ? t('inventory.update') : t('inventory.save')}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <Card className="border-0 shadow-md">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>{t('suppliers.name')}</TableHead>
                          <TableHead>{t('suppliers.leadTimeDays')}</TableHead>
                          <TableHead>{t('suppliers.linkedProducts')}</TableHead>
                          <TableHead>{t('suppliers.activeOrders')}</TableHead>
                          <TableHead className="text-right">{t('inventory.actions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loadingSuppliers ? (
                          <TableRow>
                            <TableCell colSpan={5} className="text-center py-8 text-slate-500">{t('inventory.loading')}</TableCell>
                          </TableRow>
                        ) : supplierStats.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={5} className="py-12 text-center">
                              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                              <p className="text-slate-600">{t('suppliers.noSuppliers')}</p>
                              <p className="mt-2 text-sm text-slate-400">{t('suppliers.noSuppliersDesc')}</p>
                            </TableCell>
                          </TableRow>
                        ) : (
                          supplierStats.map((supplier) => (
                            <TableRow key={supplier.id} className="hover:bg-slate-50">
                              <TableCell className="font-medium">{supplier.name}</TableCell>
                              <TableCell className="text-slate-600">
                                {supplier.lead_time_days != null ? t('suppliers.days', { count: supplier.lead_time_days }) : '—'}
                              </TableCell>
                              <TableCell className="text-slate-600">{supplier.productCount}</TableCell>
                              <TableCell className="text-slate-600">{supplier.activeOrderCount}</TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-blue-600 hover:bg-blue-50 hover:text-blue-700" onClick={() => handleEditSupplier(supplier)}>
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => setDeleteSupplierId(supplier.id)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* ─── TAB: LOSSES ─── */}
            <TabsContent value="losses">
              {/* Weekly summary */}
              <div className="mb-4 px-4 py-3 rounded-lg bg-slate-50 border border-slate-200">
                <span className="text-sm text-slate-600">
                  {t('stock.weekSummary', { count: weekLossSummary.count, cost: weekLossSummary.cost.toFixed(2) })}
                </span>
              </div>

              <Card className="border-0 shadow-md">
                <CardContent className="p-0">
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead>{t('inventory.date')}</TableHead>
                          <TableHead>{t('inventory.product')}</TableHead>
                          <TableHead>{t('inventory.quantity')}</TableHead>
                          <TableHead>{t('inventory.reason')}</TableHead>
                          <TableHead>{t('inventory.shift')}</TableHead>
                          <TableHead>{t('inventory.cost')}</TableHead>
                          <TableHead>{t('inventory.reportedBy')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loadingLosses ? (
                          <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">{t('inventory.loading')}</TableCell></TableRow>
                        ) : losses.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-12">
                              <AlertCircle className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                              <p className="text-slate-500">{t('inventory.noLosses')}</p>
                              <p className="text-sm text-slate-400 mt-2">{t('inventory.noLossesDesc')}</p>
                            </TableCell>
                          </TableRow>
                        ) : (
                          losses.map((loss) => (
                            <TableRow key={loss.id} className="hover:bg-slate-50">
                              <TableCell className="font-medium">{format(new Date(loss.date), 'dd.MM.yyyy')}</TableCell>
                              <TableCell>{loss.inventory_item_name}</TableCell>
                              <TableCell>
                                <span className="font-semibold text-red-600">-{loss.quantity} {loss.unit}</span>
                              </TableCell>
                              <TableCell>
                                <Badge className={reasonColors[loss.reason] || 'bg-slate-100 text-slate-700'}>{getReasonLabel(loss.reason)}</Badge>
                              </TableCell>
                              <TableCell>
                                <span className="text-sm text-slate-600">
                                  {loss.shift === 'Mittag' ? t('inventory.lunch') : loss.shift === 'Abend' ? t('inventory.dinner') : loss.shift}
                                </span>
                              </TableCell>
                              <TableCell className="font-semibold text-red-600">{loss.cost?.toFixed(2) || '0.00'}</TableCell>
                              <TableCell className="text-slate-600">{loss.reported_by || '-'}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* ═══ NEW ORDER DIALOG ═══ */}
      <Dialog open={newOrderDialog} onOpenChange={setNewOrderDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("orders.newOrder")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>{t("orders.product")}</Label>
              <Select
                value={orderForm.inventory_item_id ? String(orderForm.inventory_item_id) : 'manual'}
                onValueChange={handleInventoryItemSelect}
              >
                <SelectTrigger>
                  <SelectValue>
                    {orderForm.inventory_item_id
                      ? inventory.find(i => i.id === orderForm.inventory_item_id)?.name || String(orderForm.inventory_item_id)
                      : t("orders.manualEntry")}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manual">{t("orders.manualEntry")}</SelectItem>
                  {inventory.map(item => (
                    <SelectItem key={item.id} value={String(item.id)}>
                      {item.name}{item.article_number ? ` · ${item.article_number}` : ''} ({item.unit})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("orders.productName")} *</Label>
                <Input value={orderForm.inventory_item_name} onChange={(e) => setOrderForm(f => ({ ...f, inventory_item_name: e.target.value }))} placeholder={t("orders.productNamePlaceholder")} />
              </div>
              <div className="space-y-2">
                <Label>{t("orders.articleNumber")}</Label>
                <Input
                  value={orderForm.article_number}
                  onChange={(e) => setOrderForm(f => ({ ...f, article_number: e.target.value }))}
                  placeholder={t("orders.articleNumberPlaceholder")}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{selectedOrderItem?.purchase_unit_quantity ? t("orders.orderCount") : t("orders.quantity")} *</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={orderForm.suggested_quantity}
                    onChange={(e) => {
                      const value = e.target.value;
                      setOrderForm((f) => {
                        if (!selectedOrderItem || !value) {
                          return { ...f, suggested_quantity: value };
                        }
                        const draft = getOrderDraft(selectedOrderItem, parseFloat(value));
                        return {
                          ...f,
                          suggested_quantity: value,
                          estimated_cost: draft.estimatedCost.toFixed(2),
                        };
                      });
                    }}
                  />
                  {selectedOrderDraft?.usesPurchaseUnits && (
                    <p className="text-xs text-slate-500">
                      {selectedOrderDraft.summary} · {formatInventoryNumber(selectedOrderDraft.coverageQuantity)} {orderForm.unit}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label>{t("orders.packageLabel")}</Label>
                  <Input
                    value={orderForm.package_label}
                    onChange={(e) => setOrderForm(f => ({ ...f, package_label: e.target.value }))}
                    placeholder={t("orders.packageLabelPlaceholder")}
                  />
                </div>
            </div>

            <div className="space-y-2">
              <Label>{t("orders.unit")} *</Label>
              <Input value={orderForm.unit} onChange={(e) => setOrderForm(f => ({ ...f, unit: e.target.value }))} placeholder="kg, L, pcs..." />
            </div>

            <div className="space-y-2">
              <Label>{t("orders.supplier")}</Label>
              <Select value={orderForm.supplier_id || 'none'} onValueChange={handleOrderSupplierSelect}>
                <SelectTrigger>
                  <SelectValue placeholder={t('suppliers.selectSupplier')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">{t('suppliers.noSupplier')}</SelectItem>
                  {suppliers.map((supplier) => (
                    <SelectItem key={supplier.id} value={String(supplier.id)}>
                      {supplier.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-slate-500">{t('suppliers.orderSupplierHint')}</p>
                <Button type="button" variant="ghost" size="sm" className="h-auto px-0 text-xs text-blue-600 hover:text-blue-700" onClick={openSupplierManager}>
                  {t('suppliers.manageSuppliers')}
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("orders.orderedOn")}</Label>
                <Input
                  type="date"
                  value={orderForm.order_date}
                  onChange={(e) => {
                    const nextOrderDate = e.target.value;
                    setOrderForm((form) => {
                      const selectedSupplier = suppliers.find((supplier) => String(supplier.id) === String(form.supplier_id));
                      return {
                        ...form,
                        order_date: nextOrderDate,
                        expected_delivery_date:
                          getExpectedDeliveryDate(nextOrderDate, selectedSupplier?.lead_time_days) || form.expected_delivery_date,
                      };
                    });
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>{t("orders.delivery")}</Label>
                <Input type="date" min={orderForm.order_date} value={orderForm.expected_delivery_date} onChange={(e) => setOrderForm(f => ({ ...f, expected_delivery_date: e.target.value }))} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t("orders.cost")}</Label>
                <Input type="number" min="0" step="0.01" value={orderForm.estimated_cost} onChange={(e) => setOrderForm(f => ({ ...f, estimated_cost: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>{t("orders.status")}</Label>
                <Select value={orderForm.status || t("orders.statusOrdered")} onValueChange={(v) => setOrderForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={t("orders.statusOrdered")}>{t("orders.statusOrdered")}</SelectItem>
                    <SelectItem value={t("orders.statusArriving")}>{t("orders.statusArriving")}</SelectItem>
                    <SelectItem value={t("orders.statusDelivered")}>{t("orders.statusDelivered")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("orders.notes")}</Label>
              <Textarea value={orderForm.notes} onChange={(e) => setOrderForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setNewOrderDialog(false)}>{t("orders.cancel")}</Button>
              <Button onClick={handleSubmitNewOrder} disabled={!isFormValid || createSingleOrderMutation.isPending} className="bg-blue-600 hover:bg-blue-700">
                {t("orders.saveOrder")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
